import { Link, useLocation } from "react-router-dom";
import { useMemo, useState } from "react";
import {
  useAssessmentsQuery,
  useCreateStudentMutation,
  useStudentsQuery,
} from "@/hooks/useStudents";
import { formatDate } from "@/utils/date";
import type { Assessment, Student } from "@/types/assessment";
import { StudentFormDialog, type StudentFormValues } from "@/components/students/StudentFormDialog";
import {
  assessmentEntryMode,
  assessmentRecordLabel,
  entryModeLabel,
  studentHasReport,
  studentCardStatus,
  studentRecommendedAction,
  studentWorkspacePaths,
  type StudentCardStatus,
} from "@/utils/student-workspace";

type StudentFilter = "all" | StudentCardStatus;
const PINNED_STUDENT_NAME = "沈安然";

const FILTERS: Array<{ value: StudentFilter; label: string }> = [
  { value: "all", label: "全部" },
  { value: "assessment", label: "评估待继续" },
  { value: "report", label: "已有评估报告" },
  { value: "new", label: "尚未评估" },
];

export function StudentsPage(): JSX.Element {
  const location = useLocation();
  const studentsQuery = useStudentsQuery();
  const assessmentsQuery = useAssessmentsQuery();
  const createStudentMutation = useCreateStudentMutation();

  const [formOpen, setFormOpen] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<StudentFilter>("all");

  const filteredStudents = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase("zh-CN");
    const assessments = assessmentsQuery.data ?? [];
    return (studentsQuery.data ?? [])
      .filter((student) => {
        const searchable = [
          student.profile.name,
          student.enrollment.school,
          student.enrollment.classroom,
          student.enrollment.group ?? "",
        ]
          .join(" ")
          .toLocaleLowerCase("zh-CN");
        const matchesQuery = !normalizedQuery || searchable.includes(normalizedQuery);
        const status = studentCardStatus(assessments, student.id);
        const matchesFilter =
          filter === "all" ||
          (filter === "report"
            ? studentHasReport(assessments, student.id)
            : status === filter);
        return matchesQuery && matchesFilter;
      })
      .sort(
        (left, right) =>
          Number(left.profile.name !== PINNED_STUDENT_NAME) -
          Number(right.profile.name !== PINNED_STUDENT_NAME),
      );
  }, [assessmentsQuery.data, filter, query, studentsQuery.data]);

  const notice = getLocationNotice(location.state);

  function handleCreateClick() {
    setFormError(null);
    setFormOpen(true);
  }

  async function handleFormSubmit(values: StudentFormValues) {
    const payload = toStudentPayload(values);
    setFormError(null);

    try {
      await createStudentMutation.mutateAsync(payload);
      setFormOpen(false);
    } catch (error) {
      setFormError(error instanceof Error ? error.message : "保存失败，请稍后重试。");
    }
  }

  return (
    <div className="min-h-full bg-[linear-gradient(135deg,#fbfcfa_0%,#f3f7ee_52%,#f8faf5_100%)] p-4 md:p-6">
      <div className="mx-auto max-w-[1560px] space-y-6">
        {notice ? (
          <p
            role="status"
            className="rounded-xl border border-brand/20 bg-brand/5 px-4 py-3 text-sm text-brand-dark"
          >
            {notice}
          </p>
        ) : null}

        <section
          aria-label="查找儿童"
          className="rounded-2xl border border-slate-200/90 bg-white/[0.92] p-4 shadow-[0_18px_45px_-35px_rgba(51,65,85,0.45)] md:p-5"
        >
          <div className="flex flex-col gap-4 xl:flex-row xl:items-center">
            <div className="relative w-full lg:max-w-lg">
              <label htmlFor="student-search" className="sr-only">
                搜索儿童姓名、班级或学校
              </label>
              <input
                id="student-search"
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="搜索姓名、班级或学校"
                className="min-h-11 w-full rounded-xl border border-slate-300 bg-white px-4 text-sm text-slate-700 placeholder:text-slate-400 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
              />
            </div>
            <div className="flex min-w-0 flex-1 gap-2 overflow-x-auto" aria-label="按状态筛选">
              {FILTERS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  aria-pressed={filter === option.value}
                  onClick={() => setFilter(option.value)}
                  className={`inline-flex min-h-11 shrink-0 items-center rounded-lg px-4 text-sm font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 ${
                    filter === option.value
                      ? "bg-brand text-white"
                      : "border border-slate-200 bg-white text-slate-600 hover:border-brand/40 hover:text-brand-dark"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={handleCreateClick}
              className="inline-flex min-h-11 shrink-0 items-center justify-center rounded-xl bg-accent-warm px-5 text-sm font-semibold text-white shadow-[0_12px_26px_-16px_rgba(196,82,24,0.85)] transition hover:bg-accent-warm-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-warm focus-visible:ring-offset-2"
            >
              <span className="mr-1.5 text-lg leading-none" aria-hidden="true">
                +
              </span>
              新增儿童
            </button>
          </div>
        </section>

        <div className="flex items-center justify-between gap-4">
          <h2 className="text-lg font-semibold text-slate-800">儿童列表</h2>
          <p className="text-sm text-slate-500" aria-live="polite" aria-atomic="true">
            共 {filteredStudents.length} 名儿童
          </p>
        </div>

        {studentsQuery.isLoading || assessmentsQuery.isLoading ? (
          <p className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
            正在加载儿童数据…
          </p>
        ) : null}
        {studentsQuery.isError || assessmentsQuery.isError ? (
          <p
            role="alert"
            className="rounded-2xl border border-red-200 bg-red-50 p-8 text-center text-sm text-red-700"
          >
            儿童数据加载失败，请稍后重试。
          </p>
        ) : null}

        {!studentsQuery.isLoading &&
        !assessmentsQuery.isLoading &&
        !studentsQuery.isError &&
        !assessmentsQuery.isError ? (
          filteredStudents.length > 0 ? (
            <section aria-label="儿童列表" className="grid gap-6 lg:grid-cols-2 2xl:grid-cols-3">
              {filteredStudents.map((student) => (
                <StudentCard
                  key={student.id}
                  student={student}
                  assessments={assessmentsQuery.data ?? []}
                />
              ))}
            </section>
          ) : (
            <section className="rounded-2xl border border-dashed border-brand/30 bg-white/70 p-10 text-center">
              <h2 className="text-lg font-semibold text-slate-800">没有找到符合条件的儿童</h2>
              <p className="mt-2 text-sm text-slate-500">可以换一个关键词，或查看全部状态。</p>
              {filter !== "all" || query ? (
                <button
                  type="button"
                  onClick={() => {
                    setFilter("all");
                    setQuery("");
                  }}
                  className="mt-5 inline-flex min-h-11 items-center rounded-lg border border-brand px-4 text-sm font-semibold text-brand-dark hover:bg-brand/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
                >
                  清除筛选
                </button>
              ) : null}
            </section>
          )
        ) : null}
      </div>
      <StudentFormDialog
        open={formOpen}
        loading={createStudentMutation.isPending}
        error={formError}
        onClose={() => {
          if (createStudentMutation.isPending) {
            return;
          }
          setFormOpen(false);
          setFormError(null);
        }}
        onSubmit={handleFormSubmit}
      />
    </div>
  );
}

function StudentCard({
  student,
  assessments,
}: {
  student: Student;
  assessments: readonly Assessment[];
}): JSX.Element {
  const action = studentRecommendedAction(assessments, student.id);
  const status = studentCardStatus(assessments, student.id);
  const statusLabel = getStudentStatusLabel(assessments, student.id);
  const actionCopy = getActionCopy(action);
  const actionHref =
    action.type === "continue_assessment"
      ? studentWorkspacePaths.assessment(
          student.id,
          assessmentEntryMode(action.assessment),
          action.assessment.id,
        )
      : action.type === "view_report"
        ? studentWorkspacePaths.reports(student.id, action.assessment.id)
        : studentWorkspacePaths.assessments(student.id);
  const stage = getStudentCardStage(status, statusLabel);
  const accent = STUDENT_CARD_ACCENTS[stage];

  return (
    <article
      className={`group relative isolate flex min-h-[20rem] flex-col overflow-hidden rounded-[1.75rem] border bg-gradient-to-br from-white via-white shadow-[0_28px_58px_-38px_rgba(51,65,85,0.5)] transition-[transform,box-shadow,border-color] duration-150 ease-out hover:-translate-y-1 hover:shadow-[0_34px_68px_-38px_rgba(51,65,85,0.55)] ${accent.surface}`}
    >
      <Link
        to={studentWorkspacePaths.overview(student.id)}
        aria-label={`打开 ${student.profile.name} 的儿童概览`}
        className="absolute inset-0 z-10 rounded-[1.75rem] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
      />
      <span
        className={`pointer-events-none absolute right-0 top-0 -z-10 h-28 w-28 rounded-bl-[4.5rem] ${accent.block}`}
        aria-hidden="true"
      />
      <div className="flex flex-1 flex-col p-6">
        <div className="flex items-start gap-4">
          <div className="flex min-w-0 items-center gap-3">
            <span
              className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-[1.15rem] text-xl font-semibold text-white shadow-[0_12px_25px_-16px_rgba(51,65,85,0.7)] ${accent.avatar}`}
              aria-hidden="true"
            >
              {student.profile.name.slice(0, 1)}
            </span>
            <div className="min-w-0">
              <h3 className="block truncate text-xl font-semibold tracking-tight text-slate-800 transition group-hover:text-brand-dark">
                {student.profile.name}
              </h3>
              <p className="mt-1 truncate text-sm text-slate-500">
                {student.profile.age == null ? "年龄未填写" : `${student.profile.age} 岁`} ·{" "}
                {student.enrollment.classroom || "班级未填写"}
              </p>
            </div>
          </div>
        </div>

        <div className="mt-9 flex-1">
          <h4 className="text-xl font-semibold tracking-tight text-slate-800">
            {actionCopy.title}
          </h4>
          <p className="mt-2 max-w-md text-sm leading-6 text-slate-600">{actionCopy.description}</p>
        </div>

        <div className="mt-6 flex items-center justify-between gap-3 border-t border-brand/10 pt-5">
          <StatusBadge stage={stage} label={statusLabel} />
          <Link
            to={actionHref}
            className="relative z-20 inline-flex min-h-11 shrink-0 items-center justify-center rounded-xl bg-brand px-5 text-sm font-semibold text-white shadow-[0_12px_24px_-15px_rgba(63,101,29,0.95)] hover:bg-brand-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
          >
            {actionCopy.actionLabel}
            <span className="ml-1" aria-hidden="true">
              →
            </span>
          </Link>
        </div>
      </div>
    </article>
  );
}

type StudentCardStage = "new" | "assessment" | "report" | "growth";

const STUDENT_CARD_ACCENTS: Record<
  StudentCardStage,
  { surface: string; block: string; avatar: string }
> = {
  new: {
    surface: "border-brand/20 to-brand/[0.06] hover:border-brand/35",
    block: "bg-brand/[0.09]",
    avatar: "bg-brand",
  },
  assessment: {
    surface: "border-lime-200/90 to-lime-50/70 hover:border-lime-300",
    block: "bg-lime-100/80",
    avatar: "bg-lime-700",
  },
  growth: {
    surface: "border-emerald-200/90 to-emerald-50/70 hover:border-emerald-300",
    block: "bg-emerald-100/80",
    avatar: "bg-emerald-700",
  },
  report: {
    surface: "border-teal-200/80 to-accent-fresh-soft hover:border-teal-300",
    block: "bg-teal-100/80",
    avatar: "bg-teal-700",
  },
};

function getStudentCardStage(status: StudentCardStatus, label: string): StudentCardStage {
  if (status === "new") return "new";
  if (status === "assessment") return "assessment";
  return label.includes("阶段变化") ? "growth" : "report";
}

function StatusBadge({ stage, label }: { stage: StudentCardStage; label: string }): JSX.Element {
  const palette =
    stage === "assessment"
      ? "bg-lime-100 text-lime-800"
      : stage === "report"
        ? "bg-teal-100 text-teal-800"
        : stage === "growth"
          ? "bg-emerald-100 text-emerald-800"
          : "bg-brand/10 text-brand-dark";
  return (
    <span className={`shrink-0 rounded-full px-3 py-1 text-xs font-semibold ${palette}`}>
      {label}
    </span>
  );
}

function getStudentStatusLabel(assessments: readonly Assessment[], studentId: string): string {
  const studentAssessments = assessments.filter((assessment) => assessment.studentId === studentId);
  const inProgress = studentAssessments
    .filter((assessment) => assessment.status === "in_progress")
    .sort(
      (left, right) => new Date(right.startedAt).getTime() - new Date(left.startedAt).getTime(),
    );
  if (inProgress[0]) {
    return `${assessmentRecordLabel()}进行中`;
  }

  const completed = studentAssessments
    .filter((assessment) => assessment.status === "completed")
    .sort(
      (left, right) =>
        new Date(right.submittedAt ?? right.startedAt).getTime() -
        new Date(left.submittedAt ?? left.startedAt).getTime(),
    );
  if (completed.length > 1) {
    return "可查看阶段变化";
  }
  if (completed[0]) {
    return `${assessmentRecordLabel()}已完成`;
  }
  return "尚未评估";
}

function getActionCopy(action: ReturnType<typeof studentRecommendedAction>): {
  title: string;
  description: string;
  actionLabel: string;
} {
  if (action.type === "continue_assessment") {
    const mode = assessmentEntryMode(action.assessment);
    return {
      title: `继续${entryModeLabel(mode)}`,
      description: `这份评估开始于 ${formatDate(action.assessment.startedAt)}，可从已保存进度继续。`,
      actionLabel: mode === "activity" ? "继续活动评估" : "继续评分",
    };
  }
  if (action.type === "view_report") {
    return {
      title: "查看最新评估结果",
      description: `${formatDate(action.assessment.submittedAt ?? action.assessment.startedAt)} 完成${assessmentRecordLabel()}，可查看报告与后续计划。`,
      actionLabel: "查看报告",
    };
  }
  return {
    title: "开始首次评估",
    description: "尚无评估记录，可根据现场条件选择完整量表或活动评估。",
    actionLabel: "选择评估方式",
  };
}

function getLocationNotice(state: unknown): string | null {
  if (!state || typeof state !== "object" || !("notice" in state)) {
    return null;
  }
  return typeof state.notice === "string" ? state.notice : null;
}

function toStudentPayload(values: StudentFormValues) {
  return {
    name: values.name.trim(),
    gender: values.gender,
    birthdate: values.birthdate,
    school: values.school.trim(),
    classroom: values.classroom.trim(),
    group: values.group.trim(),
    enrollmentDate: toApiDateFromMonth(values.enrollmentDate),
    disabilityCategory: values.disabilityCategory.trim(),
    disabilityCause: values.disabilityCause.trim(),
    disabilityLevel: values.disabilityLevel.trim(),
    guardians: values.guardians
      .map((guardian) => ({
        name: guardian.name.trim(),
        relationship: guardian.relationship.trim(),
        address: guardian.address.trim(),
        occupation: guardian.occupation.trim(),
        phone: guardian.phone.trim(),
        email: guardian.email.trim(),
      }))
      .filter((guardian) => guardian.name.length > 0),
    notes: values.notes.trim(),
  };
}

function toApiDateFromMonth(value: string): string {
  if (!value) {
    return "";
  }

  return /^\d{4}-\d{2}$/.test(value) ? `${value}-01` : value;
}
